<template>
  <a-card class="margin-top-20" title="产量数据分析" :headStyle="headStyle" :loading="loading">
    <template #extra>
      <a-button type="primary" size="small" @click="showIndividualModal">
        添加个体产量记录
      </a-button>
    </template>
    
    <!-- 统计卡片区域 -->
    <div class="stats-cards" v-if="!statsLoading">
      <div class="stat-card">
        <div class="stat-icon milk">
          <a-icon type="database" />
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ formatNumber(monthlyStats.total_milk_yield) }}</div>
          <div class="stat-label">近一个月产奶量(kg)</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon fat">
          <a-icon type="percentage" />
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ formatNumber(monthlyStats.avg_fat_rate) }}</div>
          <div class="stat-label">近一个月乳脂率(%)</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon dry">
          <a-icon type="experiment" />
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ formatNumber(monthlyStats.avg_dry_mass) }}</div>
          <div class="stat-label">近一个月干物质(kg)</div>
        </div>
      </div>
    </div>
    <div class="stats-loading" v-else>
      <a-spin size="small" />
    </div>
    
    <a-tabs v-model="activeKey" @change="handleTabChange">
      <a-tab-pane key="heatmap" tab="生产热图">
        <div class="heatmap-section">
          <div class="heatmap-controls">
            <a-select 
              v-model="selectedYear" 
              style="width: 120px; margin-right: 10px;"
              @change="loadHeatmapData">
              <a-select-option 
                v-for="year in availableYears" 
                :key="year" 
                :value="year">
                {{ year }}年
              </a-select-option>
            </a-select>
            <a-button @click="refreshHeatmap">刷新</a-button>
          </div>
          <div v-if="heatmapLoading" class="loading-container">
            <a-spin tip="加载中..." />
          </div>
          <a-empty v-else-if="!heatmapData || heatmapData.length === 0" description="暂无热图数据" />
          <HeatmapCalendar
            v-else
            title=""
            :data="heatmapData"
            :year="selectedYear"
            :color-levels="['#ebedf0', '#c6e48b', '#7bc96f', '#239a3b', '#196127']"
            legend-less-text="低产"
            legend-more-text="高产"
            @cell-click="handleHeatmapClick"
          />
        </div>
      </a-tab-pane>
      <a-tab-pane key="chart" tab="图表">
        <div v-if="chartLoading" class="loading-container">
          <a-spin tip="加载中..." />
        </div>
        <a-empty v-else-if="!chartData" description="暂无数据" />
        <chart-line :height="350" :data="chartData" v-else />
      </a-tab-pane>
      
      
      <a-tab-pane key="table" tab="表格">
        <div v-if="tableLoading" class="loading-container">
          <a-spin tip="加载中..." />
        </div>
        <a-empty v-else-if="tableData.length === 0" description="暂无数据" />
        <a-table 
          v-else
          :columns="columns" 
          :dataSource="tableData"
          :pagination="pagination"
          :rowKey="record => record.id"
          @change="handleTableChange"
        > 
          <template slot="product_weight" slot-scope="text">
            {{ text || '-' }}
          </template>
          <template slot="fat_rate" slot-scope="text">
            {{ text || '-' }}
          </template>
          <template slot="dry_mass" slot-scope="text">
            {{ text || '-' }}
          </template>
          <template slot="record_type" slot-scope="text">
            {{ text === 1 ? '实际记录' : text === 2 ? '估算记录' : '-' }}
          </template>
          <template slot="recorded_at" slot-scope="text">
            {{ text || '-' }}
          </template>
          <template slot="operations" slot-scope="text, record">
            <a-popconfirm
              title="确定删除这条产量记录吗？"
              ok-text="确定"
              cancel-text="取消"
              @confirm="handleDelete(record.id)"
            >
              <a-button size="small" type="danger">
                <a-icon type="delete" />
                删除
              </a-button>
            </a-popconfirm>
          </template>
        </a-table>
      </a-tab-pane>
    </a-tabs>
    


    <!-- 个体产量记录新增模态框 -->
    <a-modal
      title="添加个体产量记录"
      :visible="isIndividualModalVisible"
      @ok="handleIndividualOk"
      @cancel="handleIndividualCancel"
      :confirmLoading="individualSubmitLoading"
    >
      <a-form-model
        ref="individualFormRef"
        :model="individualForm"
        :rules="individualRules"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 16 }"
      >
        <a-form-model-item label="产量(千克)" prop="product_weight">
          <a-input-number 
            v-model="individualForm.product_weight" 
            placeholder="请输入产量"
            style="width: 100%"
            :precision="2"
          />
        </a-form-model-item>
        
        <a-form-model-item label="乳脂率(%)" prop="fat_rate">
          <a-input-number 
            v-model="individualForm.fat_rate" 
            placeholder="请输入乳脂率"
            style="width: 100%"
            :precision="2"
          />
        </a-form-model-item>
        
        <a-form-model-item label="干物质(千克)" prop="dry_mass">
          <a-input-number 
            v-model="individualForm.dry_mass" 
            placeholder="请输入干物质"
            style="width: 100%"
            :precision="2"
          />
        </a-form-model-item>
        
        <a-form-model-item label="记录类型" prop="record_type">
          <a-select v-model="individualForm.record_type" placeholder="请选择记录类型">
            <a-select-option :value="1">实际记录</a-select-option>
            <a-select-option :value="2">估算记录</a-select-option>
          </a-select>
        </a-form-model-item>
        
        <a-form-model-item label="记录日期" prop="recorded_at">
          <a-date-picker 
            v-model="individualForm.recorded_at" 
            style="width: 100%"
            placeholder="请选择记录日期"
            valueFormat="YYYY-MM-DD"
          />
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </a-card>
</template>

<script>
import ChartLine from "@/components/Charts/ChartLine.vue";
import HeatmapCalendar from "@/components/Widgets/HeatmapCalendar.vue";

export default {
  name: "ProductionSection",
  components: {
    ChartLine,
    HeatmapCalendar
  },

  props: {
    itemId: {
      type: Number,
      required: true,
    },
    farmId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      activeKey: 'heatmap',
      chartLoading: false,
      tableLoading: false,
      heatmapLoading: false, // 热图加载状态
      loading: false,
      statsLoading: false, // 统计数据加载状态
      // 月度统计数据
      monthlyStats: {
        total_milk_yield: 0,
        avg_fat_rate: 0,
        avg_dry_mass: 0
      },
      // 热图相关数据
      heatmapData: [], // 热图数据
      selectedYear: new Date().getFullYear(), // 当前选中的年份
      availableYears: [], // 可选年份列表
      // 个体产量记录相关数据
      isIndividualModalVisible: false,
      individualSubmitLoading: false,
      individualForm: {
        item_id: '',
        farm_id: '',
        product_weight: null,
        fat_rate: null,
        dry_mass: null,
        record_type: 1,
        recorded_at: '',
      },
      tableData: [],
      pagination: {
        pageSize: 10,
        current: 1,
        total: 0,
        showTotal: total => `共 ${total} 条记录`
      },
      chartData: null,
      columns: [
        {
          title: '产量(千克)',
          dataIndex: 'product_weight',
          key: 'product_weight',
          scopedSlots: { customRender: 'product_weight' },
          width: '15%',
        },
        {
          title: '乳脂率(%)',
          dataIndex: 'fat_rate',
          key: 'fat_rate',
          scopedSlots: { customRender: 'fat_rate' },
          width: '15%',
        },
        {
          title: '干物质(千克)',
          dataIndex: 'dry_mass',
          key: 'dry_mass',
          scopedSlots: { customRender: 'dry_mass' },
          width: '15%',
        },
        {
          title: '记录类型',
          dataIndex: 'record_type',
          key: 'record_type',
          scopedSlots: { customRender: 'record_type' },
          width: '15%',
        },
        {
          title: '记录日期',
          dataIndex: 'recorded_at',
          key: 'recorded_at',
          scopedSlots: { customRender: 'recorded_at' },
          width: '20%',
        },
        {
          title: '操作',
          key: 'operations',
          scopedSlots: { customRender: 'operations' },
          width: '20%',
        },
      ],
      headStyle: {
        backgroundColor: '#2563EB',
        color: '#efefef',
        'border-radius': '10px 10px 0px 0px',
        'font-weight': 'bold',
      },

      // 个体产量记录表单验证规则
      individualRules: {
        product_weight: [
          {
            required: false,
            message: '请输入产量',
            trigger: 'blur',
          },
          {
            pattern: /^-?\d+(\.\d+)?$/,
            message: '请输入有效的数字',
            trigger: 'blur',
          },
        ],
        fat_rate: [
          {
            required: false,
            message: '请输入乳脂率',
            trigger: 'blur',
          },
          {
            pattern: /^-?\d+(\.\d+)?$/,
            message: '请输入有效的数字',
            trigger: 'blur',
          },
        ],
        dry_mass: [
          {
            required: false,
            message: '请输入干物质',
            trigger: 'blur',
          },
          {
            pattern: /^-?\d+(\.\d+)?$/,
            message: '请输入有效的数字',
            trigger: 'blur',
          },
        ],
        record_type: [
          {
            required: true,
            message: '请选择记录类型',
            trigger: 'change',
          },
        ],
        recorded_at: [
          {
            required: true,
            message: '请选择记录日期',
            trigger: 'change',
          },
        ],
      },
    };
  },
  mounted() {
    this.loadData();
  },
  methods: {
    // 加载数据
    async loadData() {
      this.loading = true; // 开启整体loading
      // 先清空数据，确保图表和表格能正确更新
      this.chartData = null;
      this.tableData = [];
      await Promise.all([
        this.loadChartData(),
        this.loadTableData(),
        this.loadHeatmapData(true),
        this.loadMonthlyStats(),
      ]);
      this.loading = false; // 关闭整体loading
    },
    
    // 加载月度统计数据
    async loadMonthlyStats() {
      this.statsLoading = true;
      try {
        const res = await this.$api.getIndividualMonthlyStats(this.itemId);
        if (res.code === 200 && res.data) {
          this.monthlyStats = res.data;
        } else {
          this.monthlyStats = {
            total_milk_yield: 0,
            avg_fat_rate: 0,
            avg_dry_mass: 0
          };
        }
      } catch (error) {
        console.error('加载月度统计数据失败:', error);
        this.monthlyStats = {
          total_milk_yield: 0,
          avg_fat_rate: 0,
          avg_dry_mass: 0
        };
      } finally {
        this.statsLoading = false;
      }
    },
    
    // 格式化数字
    formatNumber(value) {
      if (value === null || value === undefined) return '-';
      const num = parseFloat(value);
      if (isNaN(num)) return '-';
      // 如果是整数，直接返回
      if (Number.isInteger(num)) return num.toString();
      // 否则保留2位小数
      return num.toFixed(2);
    },
    
    // 显示个体产量记录模态框
    async showIndividualModal() {
      // 初始化表单
      this.individualForm = {
        item_id: this.itemId,
        farm_id: this.farmId,
        product_weight: null,
        fat_rate: null,
        dry_mass: null,
        record_type: 1,
        recorded_at: new Date().toISOString().split('T')[0], // 默认为今天
      };
      
      this.isIndividualModalVisible = true;
    },
    
    // 重置个体表单
    resetIndividualForm() {
      this.individualForm = {
        item_id: this.itemId,
        farm_id: this.farmId,
        product_weight: null,
        fat_rate: null,
        dry_mass: null,
        record_type: 1,
        recorded_at: new Date().toISOString().split('T')[0],
      };
      this.$nextTick(() => {
        if (this.$refs.individualFormRef) {
          this.$refs.individualFormRef.clearValidate();
        }
      });
    },
    
    // 加载图表数据
    async loadChartData() {
      this.chartLoading = true;
      try {
        const res = await this.$api.getProductionChartData({
          item_id: this.itemId,
          days: 30, // 最近30天
        });
        // 将数据格式化为图表所需格式
        if (res.code === 200) {
          this.chartData = res.data?.map(item => ({
            x: item.recorded_at,
            y: parseFloat(item.product_weight) || 0,
            name: item.recorded_at
          })) || [];
        } else {
          this.chartData = [];
        }
      } catch (error) {
        console.error('加载产量图表数据失败:', error);
        this.$message.error('加载产量图表数据失败');
        this.chartData = [];
      } finally {
        this.chartLoading = false;
      }
    },
    
    // 加载表格数据
    async loadTableData(page_no = 1, page_size = 10) {
      this.tableLoading = true;
      try {
        const res = await this.$api.get_production_records({
          item_id: this.itemId,
          page_no: page_no,
          page_size: page_size,
          start_at: this.getDateNDaysAgo(30), // 最近30天
          end_at: new Date().toISOString().split('T')[0],
        });
        
        if (res.code === 200) {
          this.tableData = res.data || [];
          this.pagination = {
            pageSize: res.page?.page_size || page_size,
            current: res.page?.page_no || page_no,
            total: res.page?.total || 0,
            showTotal: total => `共 ${total} 条记录`
          };
        } else {
          this.tableData = [];
          this.pagination = {
            pageSize: page_size,
            current: page_no,
            total: 0,
            showTotal: total => `共 0 条记录`
          };
        }
      } catch (error) {
        console.error('加载产量表格数据失败:', error);
        this.$message.error('加载产量表格数据失败');
        this.tableData = [];
        this.pagination = {
          pageSize: page_size,
          current: page_no,
          total: 0,
          showTotal: total => `共 0 条记录`
        };
      } finally {
        this.tableLoading = false;
      }
    },
    
    // 获取N天前的日期字符串
    getDateNDaysAgo(n) {
      const date = new Date();
      date.setDate(date.getDate() - n);
      return date.toISOString().split('T')[0];
    },
    
    // 格式化日期用于热图组件
    formatDateForHeatmap(dateString) {
      if (!dateString) return '';
      
      // 如果已经是 YYYY-MM-DD 格式，直接返回
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      
      // 如果是 ISO 格式，转换为 YYYY-MM-DD
      try {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } catch (error) {
        console.error('日期格式转换失败:', error);
        return dateString;
      }
    },
    
    // Tab切换
    handleTabChange(key) {
      this.activeKey = key;
      // 切换到热图tab时加载数据
      if (key === 'heatmap') {
        this.initHeatmap();
      }
    },
    
    // 初始化热图
    initHeatmap() {
      // 生成最近5年的年份选项
      const currentYear = new Date().getFullYear();
      this.availableYears = Array.from({length: 5}, (_, i) => currentYear - i);
      
      // 加载当前年份的热图数据
      this.loadHeatmapData();
    },
    
    // 加载热图数据
    async loadHeatmapData(force = false) {
      if (!force && this.activeKey !== 'heatmap') return;
      
      this.heatmapLoading = true;
      try {
        const res = await this.$api.getAnnualHeatmap({
          year: this.selectedYear,
          item_id: this.itemId
        });
        
        if (res.code === 200) {
          // 数据格式适配：后端返回的数据需要转换日期格式
          this.heatmapData = (res.data || []).map(item => ({
            ...item,
            date: this.formatDateForHeatmap(item.date)
          }));
        } else {
          this.heatmapData = [];
          this.$message.warning('暂无热图数据');
        }
      } catch (error) {
        console.error('加载热图数据失败:', error);
        this.$message.error('加载热图数据失败');
        this.heatmapData = [];
      } finally {
        this.heatmapLoading = false;
      }
    },
    
    // 刷新热图
    refreshHeatmap() {
      this.loadHeatmapData();
    },
    
    // 热图单元格点击事件
    handleHeatmapClick(cellData) {
      console.log('热图单元格点击:', cellData);
      // 可以在这里添加查看详情的逻辑
      this.$message.info(`日期: ${cellData.date}, 产量: ${cellData.value}${cellData.label ? ` (${cellData.label})` : ''}`);
    },
    
    // 表格分页变化
    handleTableChange(pagination) {
      this.loadTableData(pagination.current, pagination.pageSize);
    },
    

    
    // 处理个体产量记录提交
    handleIndividualOk() {
      this.$refs.individualFormRef.validate(async valid => {
        if (valid) {
          this.individualSubmitLoading = true;
          await this.submitIndividualRecord();
        }
      });
    },
    
    // 提交个体记录
    async submitIndividualRecord() {
      try {
        const res = await this.$api.post_production_record(this.individualForm);
        if (res.code === 200) {
          this.$message.success('添加个体产量记录成功');
          this.isIndividualModalVisible = false;
          // 重新加载数据，确保图表和表格都刷新
          await this.loadData();
          // 通知父组件刷新时间线
          this.$emit('record-added');
        } else {
          this.$message.error(res.message || '添加失败');
        }
      } catch (error) {
        console.error('提交个体产量记录失败:', error);
        this.$message.error('添加失败');
      } finally {
        this.individualSubmitLoading = false;
      }
    },
    
    // 取消个体记录表单
    handleIndividualCancel() {
      this.isIndividualModalVisible = false;
    },
    

    
    // 删除记录
    handleDelete(id) {
      const _self = this;
      this.$confirm({
        title: '确定删除该条产量记录吗？',
        content: '删除后数据将无法恢复',
        okText: '确定',
        okType: 'danger',
        cancelText: '取消',
        onOk() {
          return _self.$api.deleteProductionRecord(id)
            .then((res) => {
              if (res.code === 200) {
                _self.$message.success('删除成功');
                // 重新加载数据，确保图表和表格都刷新
                _self.loadData();
                // 通知父组件刷新时间线
                _self.$emit('record-added');
              } else {
                _self.$message.error(res.message || '删除失败');
              }
            })
            .catch(error => {
              console.error('删除失败:', error);
              _self.$message.error('删除失败');
            });
        },
      });
    },
  },
};
</script>

<style scoped>
.margin-top-20 {
  margin-top: 20px;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.heatmap-section {
  padding: 20px 0;
}

.heatmap-controls {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

/* 卡片标题样式 */
.card-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.title-icon {
  font-size: 16px;
  color: #fff;
}

/* 统计卡片区域 */
.stats-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f5f7fa;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.3s ease;
}

.stat-card:hover {
  background: #e8f4ff;
  transform: translateY(-2px);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 20px;
}

.stat-icon.milk {
  background: linear-gradient(135deg, #2563EB 0%, #40a9ff 100%);
}

.stat-icon.fat {
  background: linear-gradient(135deg, #faad14 0%, #ffc53d 100%);
}

.stat-icon.dry {
  background: linear-gradient(135deg, #52c41a 0%, #73d13d 100%);
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #262626;
  line-height: 1.2;
}

.stat-label {
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 4px;
}

.stats-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80px;
  margin-bottom: 20px;
}

@media (max-width: 768px) {
  .stats-cards {
    grid-template-columns: 1fr;
  }
}

/* 热图容器样式 */
::v-deep .heatmap-calendar-widget {
  border: 1px solid #e8e8e8;
  border-radius: 4px;
}

::v-deep .heatmap-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e8e8e8;
}

::v-deep .heatmap-title {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
}

::v-deep .heatmap-legend {
  display: flex;
  align-items: center;
  gap: 8px;
}

::v-deep .legend-item {
  width: 12px;
  height: 12px;
  border-radius: 2px;
}

::v-deep .legend-label {
  font-size: 12px;
  color: #666;
}
</style>